/**
 * Flatas høyde når tastaturet er oppe.
 *
 * ========  TOPPSTRIPA BLE DYTTET UT  ========
 *
 * Brukeren 03.11.2026, om en iPad: «Christina har nå skrevet en stund på
 * liveloggen, og tidskoden i toppstripa er ikke lenger synlig … for meg så
 * det ut som at toppstripa ble dyttet ut ettersom hun skrev nok poster til å
 * dekke hele feltet.»
 *
 * Og det er nøyaktig det som skjer. Skallet er `height: 100%`, altså
 * LAYOUT-viewporten — og den krymper ikke når tastaturet kommer opp på iOS,
 * bare den visuelle. Innholdet er da høyere enn det man ser, og Safari
 * panorerer siden for å få det fokuserte feltet fram: toppstripa går ut
 * ovenfor, og den kommer ikke tilbake før tastaturet gjør det.
 *
 * Løsningen er den samme som i feltappen (`apps/mobile/src/state/kbd.ts`):
 * flata får høyden sin fra `visualViewport`, slik at den krymper i takt med
 * tastaturet. Da finnes det ingenting å panorere for.
 *
 * ========  HVORFOR EN MINDRE UTGAVE ENN APPENS  ========
 *
 * Appens fil bærer et anslag satt i selve trykket, fordi feltappen monterer
 * skrivefeltet med `autoFocus` og iOS avgjør panoreringen i det øyeblikket
 * fokus settes — den rekker ikke å måle. Her skriver man i et felt som
 * allerede står på skjermen, og målingen kommer tidsnok. Det som trengs er
 * høyden og vakta.
 */

/** Siste satte høyde. Hindrer en skriving per hendelse uten endring. */
let naa = 0;
let planlagt = false;

function sett(h: number): void {
  if (h <= 0 || h === naa) return;
  naa = h;
  document.documentElement.style.setProperty("--vvh", `${h}px`);
}

/**
 * Nuller en panorering iOS alt har gjort.
 *
 * Opprydding, ikke virkemiddel: høyden over hindrer den i å oppstå. Men
 * rekkefølgen er ikke vår — får feltet fokus før målingen er inne, har
 * Safari alt rullet, og da må den tilbake.
 *
 * I NESTE BILDE, ikke synkront: et `scrollTo` midt inne i iOS sin egen
 * rulleanimasjon blir en dragkamp som spretter forbi null. Samme erfaring
 * som i appen.
 */
function nullstillRulling(): void {
  if (window.scrollY <= 0 || planlagt) return;
  planlagt = true;
  requestAnimationFrame(() => {
    planlagt = false;
    if (window.scrollY > 0) window.scrollTo(0, 0);
  });
}

/**
 * Kobles opp én gang, fra App. Returnerer oppryddingen.
 *
 * Brukes som `useEffect(foelgViewport, [])` — derfor returtypen.
 */
export function foelgViewport(): () => void {
  const vv = window.visualViewport;

  const les = (): void => {
    /* Rundes ned. iOS gir brudne piksler, og en flate en halv piksel for
       høy legger en hårfin stripe bakgrunn under tastaturet. */
    sett(Math.floor(vv?.height ?? window.innerHeight));
    nullstillRulling();
  };

  les();
  vv?.addEventListener("resize", les);
  vv?.addEventListener("scroll", les);
  window.addEventListener("resize", les);
  window.addEventListener("orientationchange", les);
  window.addEventListener("scroll", nullstillRulling, { passive: true });

  return () => {
    vv?.removeEventListener("resize", les);
    vv?.removeEventListener("scroll", les);
    window.removeEventListener("resize", les);
    window.removeEventListener("orientationchange", les);
    window.removeEventListener("scroll", nullstillRulling);
  };
}
